/* Inline live search for the "Search" header layout — fetches matching
   posts/tags/authors from the Content API and renders them in a dropdown
   under the input, instead of opening Ghost's native search modal. */
function heroSearch() {
    const forms = document.querySelectorAll('[data-hero-search]');
    if (!forms.length) return;

    forms.forEach(function (form) {
        const input = form.querySelector('[data-hero-search-input]');
        const resultsEl = form.querySelector('[data-hero-search-results]');
        if (!input || !resultsEl) return;

        const apiKey = form.dataset.apiKey;
        const apiUrl = form.dataset.apiUrl;
        if (!apiKey || !apiUrl) return;

        const baseUrl = apiUrl.endsWith('/') ? apiUrl : apiUrl + '/';
        // This site is served pass-through behind two hostnames (marketplace.ozegen.ai
        // and ozegen.ai), but `content_api_url` and every url the Content API returns
        // are always rooted at Ghost's one configured canonical domain. Used as-is,
        // clicking a result would silently hop the visitor onto that other domain.
        // Keep them on the domain they're actually browsing by rewriting every link's
        // origin to `window.location.origin`, reusing only the path Ghost gave us.
        const mountPath = new URL(baseUrl).pathname.replace(/ghost\/api\/content\/?$/, '');

        function toCurrentOrigin(absoluteUrl) {
            const parsed = new URL(absoluteUrl);
            return `${window.location.origin}${parsed.pathname}${parsed.search}${parsed.hash}`;
        }

        // Ghost's standalone /authors/ endpoint returns a stale/incorrect `url`
        // (seen resolving to /404/) even though the same author embedded on a
        // post's `primary_author`/`authors` resolves correctly. Build the author
        // link ourselves from routes.yaml's `/author/{slug}/` taxonomy pattern
        // instead of trusting that field.
        function authorUrl(slug) {
            return `${window.location.origin}${mountPath}author/${slug}/`;
        }

        const labels = {
            posts: form.dataset.labelPosts || 'Posts',
            tags: form.dataset.labelTags || 'Tags',
            authors: form.dataset.labelAuthors || 'Authors',
            empty: form.dataset.labelEmpty || 'No results found',
            error: form.dataset.labelError || 'Something went wrong, please try again.'
        };

        let debounceTimer = null;
        let requestToken = 0;

        form.addEventListener('submit', function (e) {
            e.preventDefault();
        });

        input.addEventListener('input', function () {
            clearTimeout(debounceTimer);
            const query = input.value.trim();

            if (query.length < 2) {
                requestToken += 1;
                closeResults();
                return;
            }

            debounceTimer = setTimeout(function () {
                runSearch(query);
            }, 250);
        });

        input.addEventListener('focus', function () {
            const query = input.value.trim();
            if (query.length >= 2 && resultsEl.hidden) {
                runSearch(query);
            }
        });

        input.addEventListener('keydown', function (e) {
            if (e.key === 'Escape') {
                closeResults();
                input.blur();
                return;
            }

            if (resultsEl.hidden) return;

            const items = Array.from(resultsEl.querySelectorAll('[data-hero-search-item]'));
            if (!items.length) return;

            const activeIndex = items.findIndex(function (item) {
                return item.classList.contains('is-active');
            });

            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                const nextIndex = e.key === 'ArrowDown'
                    ? (activeIndex + 1) % items.length
                    : (activeIndex - 1 + items.length) % items.length;

                items.forEach(function (item) {
                    item.classList.remove('is-active');
                });
                items[nextIndex].classList.add('is-active');
                items[nextIndex].scrollIntoView({block: 'nearest'});
            } else if (e.key === 'Enter') {
                const active = items[activeIndex] || items[0];
                if (active) {
                    e.preventDefault();
                    window.location.href = active.href;
                }
            }
        });

        form.addEventListener('focusout', function (e) {
            if (form.contains(e.relatedTarget)) return;
            closeResults();
        });

        function closeResults() {
            resultsEl.hidden = true;
            resultsEl.innerHTML = '';
            input.setAttribute('aria-expanded', 'false');
        }

        function openResults() {
            resultsEl.hidden = false;
            input.setAttribute('aria-expanded', 'true');
        }

        function escapeFilterValue(value) {
            return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
        }

        function buildApiUrl(resource, query) {
            const params = new URLSearchParams();
            params.set('key', apiKey);
            params.set('limit', '5');

            const filterValue = escapeFilterValue(query);

            if (resource === 'posts') {
                params.set('filter', `title:~'${filterValue}'`);
                params.set('fields', 'title,url,feature_image');
                params.set('order', 'published_at desc');
            } else if (resource === 'tags') {
                params.set('filter', `name:~'${filterValue}'+visibility:public`);
                params.set('fields', 'name,url');
            } else {
                params.set('filter', `name:~'${filterValue}'`);
                params.set('fields', 'name,slug,profile_image');
            }

            return `${baseUrl}${resource}/?${params.toString()}`;
        }

        async function runSearch(query) {
            const token = ++requestToken;

            try {
                const [postsRes, tagsRes, authorsRes] = await Promise.all([
                    fetch(buildApiUrl('posts', query)),
                    fetch(buildApiUrl('tags', query)),
                    fetch(buildApiUrl('authors', query))
                ]);

                const [postsData, tagsData, authorsData] = await Promise.all([
                    postsRes.ok ? postsRes.json() : {posts: []},
                    tagsRes.ok ? tagsRes.json() : {tags: []},
                    authorsRes.ok ? authorsRes.json() : {authors: []}
                ]);

                if (token !== requestToken) return;

                renderResults({
                    posts: postsData.posts || [],
                    tags: tagsData.tags || [],
                    authors: authorsData.authors || []
                });
            } catch (e) {
                if (token !== requestToken) return;
                renderError();
            }
        }

        function renderError() {
            resultsEl.innerHTML = '';
            resultsEl.appendChild(buildMessage(labels.error));
            openResults();
        }

        function renderResults(groups) {
            resultsEl.innerHTML = '';

            if (!groups.posts.length && !groups.tags.length && !groups.authors.length) {
                resultsEl.appendChild(buildMessage(labels.empty));
                openResults();
                return;
            }

            appendGroup(labels.posts, groups.posts, function (post) {
                return buildItem(toCurrentOrigin(post.url), post.feature_image, post.title);
            });
            appendGroup(labels.tags, groups.tags, function (tag) {
                return buildItem(toCurrentOrigin(tag.url), null, tag.name);
            });
            appendGroup(labels.authors, groups.authors, function (author) {
                return buildItem(authorUrl(author.slug), author.profile_image, author.name);
            });

            openResults();
        }

        function appendGroup(label, list, buildRow) {
            if (!list.length) return;

            const group = document.createElement('div');
            group.className = 'gh-hero-search-group';

            const heading = document.createElement('p');
            heading.className = 'gh-hero-search-label';
            heading.textContent = label;
            group.appendChild(heading);

            list.forEach(function (entry) {
                group.appendChild(buildRow(entry));
            });

            resultsEl.appendChild(group);
        }

        function buildMessage(text) {
            const message = document.createElement('p');
            message.className = 'gh-hero-search-empty';
            message.textContent = text;
            return message;
        }

        function buildItem(url, image, title) {
            const item = document.createElement('a');
            item.className = 'gh-hero-search-item';
            item.href = url;
            item.setAttribute('role', 'option');
            item.setAttribute('data-hero-search-item', '');

            if (image) {
                const img = document.createElement('img');
                img.className = 'gh-hero-search-thumb';
                img.src = image;
                img.alt = '';
                item.appendChild(img);
            }

            const text = document.createElement('span');
            text.textContent = title;
            item.appendChild(text);

            return item;
        }
    });
}
